import { posthog } from './posthog'
import {
    clearApiErrorSignatures,
    decideApiErrorCapture,
    markApiErrorCaptureFailed,
    markApiErrorCaptured,
    normalizeApiPath,
} from './api-error-signature'

// Ring buffer of the latest API errors. Powers two support flows:
// - the Sentry "Báo lỗi" report attaches these req_ids so an admin can trace
//   the exact failed calls in Loki without asking the user anything;
// - each error is also captured as a PostHog `api_error` event, linking a
//   req_id quoted from an error toast to the user's session replay.
export interface RecentApiError {
    requestId?: string
    status: number
    code: string
    method: string
    path: string
    message: string
    at: string
    // "config" when the error is a configuration mistake, not a transient
    // failure — see ApiError.kind.
    kind?: string
}

export type ApiErrorInput = Omit<RecentApiError, 'at'>

const MAX_RECENT_ERRORS = 10

const buffer: RecentApiError[] = []

export function recordApiError(input: ApiErrorInput): void {
    buffer.push({ ...input, at: new Date().toISOString() })
    if (buffer.length > MAX_RECENT_ERRORS) {
        buffer.shift()
    }
}

export function getRecentApiErrors(): RecentApiError[] {
    return [...buffer]
}

export function clearRecentApiErrors(): void {
    buffer.length = 0
    clearApiErrorSignatures()
}

// Record + emit telemetry for one failed API call. The message stays local
// (it can contain user data); PostHog only gets routing metadata.
export function trackApiError(input: ApiErrorInput): void {
    try {
        recordApiError(input)
    } catch {
        // Telemetry must never break the error path itself.
    }
    try {
        if (!posthog.__loaded) return
        const decision = decideApiErrorCapture(input)
        if (!decision.capture) return
        try {
            posthog.capture('api_error', {
                req_id: input.requestId,
                status: input.status,
                code: input.code,
                method: input.method,
                // Raw path stays on every event so saved insights that filter
                // on `path` (camera 502, readiness 403) keep counting repeats.
                path: input.path,
                path_template: decision.pathTemplate,
                ...(decision.repeatCount === undefined
                    ? {}
                    : { repeat_count: decision.repeatCount }),
                ...(input.kind === undefined ? {} : { kind: input.kind }),
                route: window.location.pathname,
            })
        } catch {
            markApiErrorCaptureFailed(input)
            return
        }
        markApiErrorCaptured(input)
    } catch {
        // Telemetry must never break the error path itself.
    }
}

// Emit a business outcome for a status/code that the caller handles as a
// normal answer. It stays out of `api_error` and out of the support ring
// buffer, so real failures stay visible there. Each response is one event:
// the outcome counts attempts, and the raw path says which resource.
export function trackApiOutcome(event: string, input: ApiErrorInput): void {
    try {
        if (!posthog.__loaded) return
        posthog.capture(event, {
            req_id: input.requestId,
            status: input.status,
            code: input.code,
            path: input.path,
            path_template: normalizeApiPath(input.path),
            route: window.location.pathname,
        })
    } catch {
        // Telemetry must never break the error path itself.
    }
}
